/**
 * +grid: Grid Keys found in the world (docs/design/waystone-grid.md "Keys"). A key-bearing secret
 * chest the hero opens sends `gridKeyClaim` (the server checks the chest and the hero's place); rare
 * kills pay their key inside `fieldKills`. Both end in a small banner. Pure helpers + one action.
 */
import type { DeepReadonly, SimEvent, World } from '@keystone/sim';
import { GRID_KEY_SOURCES } from '@keystone/content';
import { gridKeys, gridIdleTotals, type Profile } from '@keystone/progression';
import type { ProfileStore } from '../meta/profileStore';

/** Secret ids (on `mapId`) whose chest the hero opened this step and that still owe a key. */
export function keySecretsOpened(events: readonly SimEvent[], heroId: string, mapId: string, p: Profile): string[] {
  const claimed = new Set(p.character ? (p.characters[p.character.classId]?.grid?.keyLog ?? []) : []);
  const out: string[] = [];
  for (const e of events) {
    if (e.type !== 'object_break' || e.sourceID !== heroId || !e.objectId.startsWith('secret:')) continue;
    const id = e.objectId.slice('secret:'.length);
    if (!GRID_KEY_SOURCES.secrets.includes(`${mapId}:${id}`) || claimed.has(`secret:${mapId}:${id}`)) continue;
    out.push(id);
  }
  return out;
}

/** Claim the keys of `secretIds` (hero position from the sim); `onKey` shows the banner. */
export function claimSecretKeys(store: ProfileStore, world: DeepReadonly<World>, heroId: string, mapId: string, secretIds: readonly string[], onKey: (held: number) => void): void {
  const u = world.units[heroId];
  if (!u) return;
  for (const secretId of secretIds) {
    void store.act('gridKeyClaim', { mapId, secretId, pos: { x: u.pos.x, y: u.pos.y } }).then((r) => {
      if (r.ok) onKey(gridKeys(r.profile).held);
    });
  }
}

/** Auto's wits from the hero's grid (Seasoned Instinct stars). */
export function heroWits(p: Profile): number {
  const c = p.character;
  const nodes = c ? (p.characters[c.classId]?.grid?.nodes ?? []) : [];
  return gridIdleTotals(nodes).autoWits ?? 0;
}
